import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const defaultOptions = {
  autoClose: 1500
};

const TOAST_INTERVAL = 3000;

/**
 * @typedef {'ERROR' | 'WARNING' | 'INFO' | 'SUCCESS'} ToastType
 */

/**
 * Armazena notificações que pertençam ao mesmo grupo (possuem a mesma mensagem)
 */
const groupedMessages = {
  NEW_CHAT: [],
  NEW_MESSAGE: [],
  TRANSFER_CHAT: [],
  CHAT_CLOSED: [],
  ERROR: [],
  /** DEFAULT é para mensagens que nunca precisarão serem agrupadas (alertas únicos) */
  DEFAULT: []
};

/**
 * @typedef {keyof groupedMessages} ToastAction
 */

/**
 * @typedef {Object} ToastProps
 * @property {string} message - o texto da mensagem
 * @property {boolean} permission - indica se as notificações toast estão permitidas
 * @property {ToastAction} action - indica qual em grupo a mensagem se classifica
 * @property {typeof defaultOptions} options
 */

/**
 * @typedef {Object} DesktopNotificationProps
 * @property {string} iconUrl - URL do ícone da notificação desktop
 * @property {string} title - Título da notificação desktop
 * @property {Function} onClick - Função a ser chamada quando a notificação for clicada
 * @property {boolean} desktopPermission - Se a permissão de notificação desktop foi concedida
 */

let intervalId;

class ToastFlex {
  static init() {
    if (intervalId) return;
    intervalId = setInterval(() => {
      this.processGroupedMessages(
        'TRANSFER_CHAT',
        'Conversa transferida',
        'conversas foram transferidas',
        'SUCCESS'
      );
      this.processGroupedMessages(
        'NEW_MESSAGE',
        'Você tem uma nova mensagem',
        'Novas mensagens',
        'SUCCESS'
      );
      this.processGroupedMessages(
        'NEW_CHAT',
        'Você recebeu uma nova conversa',
        'Novas conversas',
        'INFO'
      );
      this.processGroupedMessages(
        'CHAT_CLOSED',
        'Conversa encerrada',
        'conversas foram encerradas',
        'SUCCESS'
      );
    }, TOAST_INTERVAL);
  }

  /**
   * Processa mensagens agrupadas e exibe notificações.
   *
   * @param {ToastAction} action - Ação do toast que está sendo processada.
   * @param {string} singleMessage - Mensagem a ser exibida se houver apenas uma notificação.
   * @param {string} multipleMessage - Mensagem a ser exibida se houver múltiplas notificações.
   * @param {ToastType} type - Tipo de toast a ser exibido.
   * @param {string} audioUrl - URL do áudio a ser reproduzido.
   * @param {boolean} playAudio - Indica se o áudio deve ser reproduzido.
   */
  static processGroupedMessages(
    action,
    singleMessage,
    multipleMessage,
    type,
    audioUrl = null,
    playAudio = false
  ) {
    const messages = groupedMessages[action];
    const messageCount = messages?.length;

    if (messageCount > 0) {
      const messageToDisplay =
        messageCount === 1
          ? singleMessage
          : `${messageCount} ${multipleMessage}`;

      // Exibir toast
      toast[type.toLowerCase()](messageToDisplay, defaultOptions);

      // Se houver dados de notificação desktop, exibir notificação com contagem
      // Usar a mesma lógica de contagem/formato de mensagens que usamos nos toasts
      if (
        messages.some(
          (msg) =>
            msg.desktopProps && msg.desktopProps.desktopPermission === 'granted'
        )
      ) {
        // Usar o primeiro item que tenha propriedades de desktop notification
        const desktopProps = messages.find(
          (msg) =>
            msg.desktopProps && msg.desktopProps.desktopPermission === 'granted'
        ).desktopProps;

        const notification = new Notification(desktopProps.title, {
          body: messageToDisplay, // Mesmo texto usado no toast (com contagem)
          icon: desktopProps.iconUrl
        });

        if (desktopProps.onClick) {
          notification.onclick = desktopProps.onClick;
        }
      }

      const messageWithAudio = messages.find((msg) => msg.audioUrl);
      const effectiveAudioUrl = messageWithAudio?.audioUrl || audioUrl;
      const shouldPlayAudio = messageWithAudio?.playAudio || playAudio;

      if (shouldPlayAudio && effectiveAudioUrl) {
        let audio = new Audio(effectiveAudioUrl);

        const audioNotificationVolume =
          localStorage.getItem('@flexuc/audioNotificationVolume') || 1;
        audio.volume = audioNotificationVolume;
        const audioEnabled = JSON.parse(
          localStorage.getItem('@flexuc/audioEnabled')
        );
        if (audioEnabled) {
          audio.play();
        }
      }

      groupedMessages[action] = []; // Limpar as mensagens processadas
    }
  }

  /**
   * Exibe uma notificação de sucesso.
   *
   * @param {Object} props - Propriedades do toast.
   * @param {string} props.message - Mensagem a ser exibida.
   * @param {boolean} props.permission - Permissão para exibir a notificação.
   * @param {ToastAction} props.action - Ação do toast.
   * @param {typeof defaultOptions} [props.options=defaultOptions] - Opções do toast.
   * @param {DesktopNotificationProps} [props.desktopProps] - Propriedades para notificação desktop.
   * @param {string} [props.audioUrl] - URL do áudio a ser reproduzido.
   * @param {boolean} [props.playAudio] - Indica se o áudio deve ser reproduzido.
   */
  static success({
    message,
    permission,
    action,
    options = defaultOptions,
    desktopProps,
    audioUrl,
    playAudio
  }) {
    this.showToastIfAllowed({
      message,
      permission,
      action,
      options,
      type: 'success',
      desktopProps,
      audioUrl,
      playAudio
    });
  }

  /**
   * Exibe uma notificação de erro.
   *
   * @param {Object} props - Propriedades do toast.
   * @param {string} props.message - Mensagem a ser exibida.
   * @param {typeof defaultOptions} [props.options=defaultOptions] - Opções do toast.
   * @param {DesktopNotificationProps} [props.desktopProps] - Propriedades para notificação desktop.
   * @param {string} [props.audioUrl] - URL do áudio a ser reproduzido.
   * @param {boolean} [props.playAudio] - Indica se o áudio deve ser reproduzido.
   */
  static error({ message, options = defaultOptions, desktopProps }) {
    toast.error(message, options);
    this.showDesktopNotificationIfAllowed({ message, desktopProps });
  }

  /**
   * Exibe uma notificação informativa.
   *
   * @param {Object} props - Propriedades do toast.
   * @param {string} props.message - Mensagem a ser exibida.
   * @param {boolean} props.permission - Permissão para exibir a notificação.
   * @param {ToastAction} props.action - Ação do toast.
   * @param {typeof defaultOptions} [props.options=defaultOptions] - Opções do toast.
   * @param {DesktopNotificationProps} [props.desktopProps] - Propriedades para notificação desktop.
   * @param {string} [props.audioUrl] - URL do áudio a ser reproduzido.
   * @param {boolean} [props.playAudio] - Indica se o áudio deve ser reproduzido.
   */
  static info({
    message,
    permission,
    action = 'DEFAULT',
    options = defaultOptions,
    desktopProps,
    audioUrl,
    playAudio
  }) {
    this.showToastIfAllowed({
      message,
      permission,
      action,
      options,
      type: 'info',
      desktopProps,
      audioUrl,
      playAudio
    });
  }

  /**
   * Exibe uma notificação de aviso.
   *
   * @param {Object} props - Propriedades do toast.
   * @param {string} props.message - Mensagem a ser exibida.
   * @param {typeof defaultOptions} [props.options=defaultOptions] - Opções do toast.
   * @param {DesktopNotificationProps} [props.desktopProps] - Propriedades para notificação desktop.
   * @param {string} [props.audioUrl] - URL do áudio a ser reproduzido.
   * @param {boolean} [props.playAudio] - Indica se o áudio deve ser reproduzido.
   */
  static warning({ message, options = defaultOptions, desktopProps }) {
    toast.warning(message, options);
    this.showDesktopNotificationIfAllowed({ message, desktopProps });
  }

  /**
   * Exibe uma notificação padrão.
   *
   * @param {Object} props - Propriedades do toast.
   * @param {string} props.message - Mensagem a ser exibida.
   * @param {boolean} props.permission - Permissão para exibir a notificação.
   * @param {ToastAction} props.action - Ação do toast.
   * @param {typeof defaultOptions} [props.options=defaultOptions] - Opções do toast.
   * @param {DesktopNotificationProps} [props.desktopProps] - Propriedades para notificação desktop.
   * @param {string} [props.audioUrl] - URL do áudio a ser reproduzido.
   * @param {boolean} [props.playAudio] - Indica se o áudio deve ser reproduzido.
   */
  static default({
    message,
    permission,
    action,
    options = defaultOptions,
    desktopProps,
    audioUrl,
    playAudio
  }) {
    this.showToastIfAllowed({
      message,
      permission,
      action,
      options,
      type: 'default',
      desktopProps,
      audioUrl,
      playAudio
    });
  }

  /**
   * Configura as opções do toast.
   *
   * @param {typeof defaultOptions} [options=defaultOptions] - Opções do toast.
   */
  static configure(options = defaultOptions) {
    toast.configure(options);
  }

  /**
   * Exibe uma notificação desktop se permitido.
   *
   * @param {Object} props - Propriedades da notificação.
   * @param {string} props.message - Mensagem a ser exibida.
   * @param {DesktopNotificationProps} props.desktopProps - Propriedades para notificação desktop.
   */
  static showDesktopNotificationIfAllowed({ message, desktopProps }) {
    if (desktopProps && desktopProps.desktopPermission === 'granted') {
      const notification = new Notification(desktopProps.title, {
        body: message,
        icon: desktopProps.iconUrl
      });

      if (desktopProps.onClick) {
        notification.onclick = desktopProps.onClick;
      }
    }
  }

  /**
   * Exibe uma notificação de acordo com os parâmetros fornecidos.
   *
   * @param {Object} props - Propriedades do toast.
   * @param {string} props.message - Mensagem a ser exibida.
   * @param {boolean} props.permission - Permissão para exibir a notificação.
   * @param {ToastAction} props.action - Ação do toast.
   * @param {typeof defaultOptions} props.options - Opções do toast.
   * @param {ToastType} props.type - Tipo de toast a ser exibido.
   * @param {DesktopNotificationProps} props.desktopProps - Propriedades para notificação desktop.
   * @param {string} [props.audioUrl] - URL do áudio a ser reproduzido.
   * @param {boolean} [props.playAudio] - Indica se o áudio deve ser reproduzido.
   */
  static showToastIfAllowed({
    message,
    permission,
    action,
    options,
    type,
    desktopProps,
    audioUrl,
    playAudio
  }) {
    if (action === 'DEFAULT' && type in toast) {
      return toast[type](message, { autoClose: 3000, closeOnClick: true });
    }

    if (!permission) return;
    this.init();

    if (Object.keys(groupedMessages).includes(action)) {
      // Armazenamos a mensagem e os dados da notificação desktop juntos
      groupedMessages[action].push({
        message,
        desktopProps,
        audioUrl,
        playAudio
      });
      return;
    }
    toast[type](message, options);
    this.showDesktopNotificationIfAllowed({ message, desktopProps });
  }
}

export default ToastFlex;
